import React, { useState, useEffect } from 'react';
import { Radio, InputNumber, Slider } from 'antd';

const allUnits = ["grams", "ml", "cup", "tbsp", "tsp", "item"];

const MeasurementSelector = ({ onChange, mlpergcf, onepergcf, initialUnit = 'grams', initialValue }) => {
  const mlUnits = ["ml", "cup", "tbsp", "tsp"];
  const availableUnits = allUnits.filter(unit => {
    if (mlUnits.includes(unit) && (mlpergcf === null || mlpergcf === undefined)) return false;
    if (unit === "item" && (onepergcf === null || onepergcf === undefined)) return false;
    return true;
  });

  const defaultUnit = availableUnits.includes(initialUnit) ? initialUnit : availableUnits[0];

  const baseConversionOptions = {
    grams: [50, 100, 150, 200, 500, 1000],
    ml: [50, 100, 150, 200, 500, 1000],
    cup: [0.25, 0.5, 1, 1.5, 2, 5],
    tbsp: [0.5, 1, 1.5, 2, 2.5, 3],
    tsp: [0.25, 0.5, 0.75, 1, 1.5, 2],
    item: [0.5, 1, 2, 3, 4, 5],
  };

  const stepValues = {
    grams: 5,
    ml: 5,
    cup: 0.1,
    tbsp: 0.1,
    tsp: 0.1,
    item: 0.25,
  };

  const [selectedFirst, setSelectedFirst] = useState(defaultUnit);
  const [selectedSecond, setSelectedSecond] = useState(initialValue ?? baseConversionOptions[defaultUnit][0]);
  const [sliderValue, setSliderValue] = useState(selectedSecond);
  const [inputValue, setInputValue] = useState(String(selectedSecond));
  const [isOther, setIsOther] = useState(false);

  const conversionOptions = React.useMemo(() => {
    const options = baseConversionOptions[selectedFirst] ? [...baseConversionOptions[selectedFirst]] : [];
    if (isOther && !options.includes(selectedSecond)) {
      options.push("Other");
    } else if (!isOther) {
      if (!options.includes(selectedSecond)) {
        options.push(selectedSecond);
      }
      const otherIndex = options.indexOf("Other");
      if (otherIndex !== -1) options.splice(otherIndex, 1);
    }
    return options;
  }, [selectedFirst, selectedSecond, isOther]);

  useEffect(() => {
    setIsOther(false);
    const options = baseConversionOptions[selectedFirst] || [];
    const defaultVal = options[0] ?? 0;
    setSelectedSecond(defaultVal);
    setSliderValue(defaultVal);
    setInputValue(String(defaultVal));
  }, [selectedFirst]);

  useEffect(() => {
    if (selectedSecond === "Other") {
      if (onChange) onChange({ unit: selectedFirst, value: sliderValue });
    } else {
      setIsOther(false);
      setSliderValue(selectedSecond);
      setInputValue(String(selectedSecond));
      if (onChange) onChange({ unit: selectedFirst, value: selectedSecond });
    }
  }, [selectedSecond]);

  const onSliderChange = val => {
    setSliderValue(val);
    setInputValue(String(val));
    if (!isOther || val !== selectedSecond) {
      setIsOther(true);
      setSelectedSecond("Other");
      if (onChange) onChange({ unit: selectedFirst, value: val });
    } else {
      if (onChange) onChange({ unit: selectedFirst, value: val });
    }
  };

  const onInputChange = e => {
    const val = e.target.value;
    setInputValue(val);
    const num = Number(val);
    if (!isNaN(num)) {
      let clamped = num;
      if (num < 0) clamped = 0;
      const max = (baseConversionOptions[selectedFirst]?.slice(-1)[0] || 1000) * 2;
      if (num > max) clamped = max;
      setSliderValue(clamped);
      if (!isOther || clamped !== selectedSecond) {
        setIsOther(true);
        setSelectedSecond("Other");
        if (onChange) onChange({ unit: selectedFirst, value: clamped });
      } else if (onChange) {
        onChange({ unit: selectedFirst, value: clamped });
      }
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '100%' }}>
      {/* First row of units */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%', marginBottom:'16px' }}>
        <Radio.Group
          value={selectedFirst}
          onChange={e => setSelectedFirst(e.target.value)}
          style={{ display: 'flex', width: '100%' }}
        >
          {availableUnits.map(unit => (
            <Radio.Button key={unit} value={unit} style={{ flex: 1, textAlign: 'center', minWidth: 64 }}>
              {unit}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      {/* Second row options + "Other" */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
        <Radio.Group
          value={selectedSecond}
          onChange={e => {
            if (e.target.value === "Other") {
              setIsOther(true);
              setSelectedSecond("Other");
            } else {
              setIsOther(false);
              setSelectedSecond(e.target.value);
            }
          }}
          style={{ display: 'flex', width: 'max-content', minWidth: 350 }}
        >
          {(conversionOptions || []).map(option => (
            <Radio.Button
              key={option}
              value={option}
              style={{
                flex: 'none',
                minWidth: 80,
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}
            >
              {option === "Other" ? "Other" : `${option} ${selectedFirst}`}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      {/* Slider + Input container */}
      <div style={{ display: 'flex', marginTop: 40, width: '100%', alignItems: 'center' }}>
        <div style={{ flex: 1, paddingRight: 8 }}>
          <Slider
            min={0}
            max={(baseConversionOptions[selectedFirst]?.slice(-1)[0] || 1000) * 2}
            onChange={onSliderChange}
            step={stepValues[selectedFirst] ?? 1} 
            value={typeof sliderValue === 'number' ? sliderValue : 0}
          />
        </div>
        <div style={{ width: 100 }}>
          <InputNumber
            value={Number(inputValue)}
            onChange={val => {
              const valueNum = val ?? 0;
              setInputValue(String(valueNum));
              setSliderValue(valueNum);
              setIsOther(true);
              setSelectedSecond("Other");
              if (onChange) onChange({ unit: selectedFirst, value: valueNum });
            }}
            min={0}
            max={(baseConversionOptions[selectedFirst]?.slice(-1)[0] || 1000) * 2}
            step={stepValues[selectedFirst] ?? 1}
            style={{ textAlign: 'right', width: '100%' }}
            placeholder={String(selectedSecond !== "Other" ? selectedSecond : sliderValue)}
          />
        </div>
      </div>
    </div>
  );
};

export default MeasurementSelector;
